/**
 * Portal-rendered tooltip primitive.
 *
 * The body is portaled into `document.body` so it escapes ancestor
 * `overflow: auto` scroll clipping (a z-index bump alone won't help when
 * the parent clips its overflow), and positioned with `position: fixed`
 * from a trigger element's bounding rect.
 *
 * Use `useTooltipState` to wire up hover/focus on the trigger; pass the
 * resulting `pos` to `<Tooltip>` and supply your content as children.
 * Themed variants (e.g. an error/red tooltip in the CSV-import review
 * table) layer on top via the `className` prop.
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Tooltip.module.scss';

export interface TooltipPos {
  /** Distance from top of viewport, in pixels (CSS `top`). */
  top: number;
  /** Distance from right of viewport, in pixels (CSS `right`). */
  right: number;
}

/**
 * Default anchor: 6px below the trigger, right edge aligned with the
 * trigger's right edge, clamped to >=8px so the tooltip never tucks off
 * the right side on narrow modals.
 */
export function defaultTooltipPos(rect: DOMRect): TooltipPos {
  return { top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) };
}

/**
 * Open/position/hide bookkeeping for a portal-rendered tooltip:
 *   - capture the trigger's bounding rect on hover/focus
 *   - hide on Escape and on any scroll (so the tooltip doesn't float
 *     orphaned from its anchor as the user scrolls)
 *
 * `active` gates hover/focus — passing `false` makes the handlers no-ops.
 * Callers use this to suppress the tooltip on rows with nothing to say
 * (e.g. a clean import row).
 */
export function useTooltipState<T extends HTMLElement>(active: boolean) {
  const triggerRef = useRef<T>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ top: 0, right: 0 });

  useEffect(() => {
    if (!open) {
      return;
    }
    const hide = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('scroll', hide, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleEnter() {
    if (!active) {
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPos(defaultTooltipPos(rect));
    }
    setOpen(true);
  }

  function handleLeave() {
    setOpen(false);
  }

  return { triggerRef, open, pos, handleEnter, handleLeave };
}

interface TooltipProps {
  pos: TooltipPos;
  id?: string;
  /** Extra class for caller-supplied theming (e.g. an error variant). */
  className?: string;
  children: React.ReactNode;
}

export default function Tooltip({ pos, id, className, children }: TooltipProps) {
  const classes = className ? `${styles.tooltip} ${className}` : styles.tooltip;
  return ReactDOM.createPortal(
    <div className={classes} style={{ top: pos.top, right: pos.right }} role="tooltip" id={id}>
      {children}
    </div>,
    document.body,
  );
}
